import React, { useState } from 'react';
import { Eye, EyeOff, TestTube, CheckCircle, XCircle, Clock } from 'lucide-react';
import Button from '@shared/ui/Button';

interface ApiConfiguration {
  authentication: {
    type: 'none' | 'api_key' | 'bearer_token' | 'oauth' | 'basic_auth' | 'custom';
    credentials: {
      api_key?: string;
      api_secret?: string;
      bearer_token?: string;
      username?: string;
      password?: string;
      oauth_client_id?: string;
      oauth_client_secret?: string;
    };
    headers?: Record<string, string>;
  };
  endpoint: {
    base_url: string;
    version?: string;
    endpoints?: Record<string, string>;
  };
  rate_limits: {
    requests_per_hour?: number;
    requests_per_minute?: number;
    burst_limit?: number;
  };
  data_mapping?: Record<string, string>;
  filters?: Record<string, string>;
  [key: string]: any; // Allow dynamic access for updateConfig function
}

interface ApiConfigurationFormProps {
  initialConfig?: ApiConfiguration;
  onChange: (config: ApiConfiguration) => void;
  onTest?: (config: ApiConfiguration) => Promise<any>;
  dataSourceType: string;
  disabled?: boolean;
}

const ApiConfigurationForm: React.FC<ApiConfigurationFormProps> = ({
  initialConfig,
  onChange,
  onTest,
  dataSourceType,
  disabled = false
}) => {
  const [config, setConfig] = useState<ApiConfiguration>(() => ({
    authentication: {
      type: 'api_key',
      credentials: {},
      headers: {}
    },
    endpoint: {
      base_url: '',
      version: 'v1',
      endpoints: {}
    },
    rate_limits: {
      requests_per_hour: 1000,
      burst_limit: 10
    },
    data_mapping: {},
    filters: {},
    ...initialConfig
  }));

  const [showCredentials, setShowCredentials] = useState<Record<string, boolean>>({});
  const [testResult, setTestResult] = useState<any>(null);
  const [testing, setTesting] = useState(false);

  // Government API presets
  const getPresetConfig = (apiName: string): Partial<ApiConfiguration> => {
    const presets: Record<string, Partial<ApiConfiguration>> = {
      colorado: {
        authentication: {
          type: 'none',
          credentials: {},
          headers: {}
        },
        endpoint: {
          base_url: 'https://data.colorado.gov/resource/yk7f-aegj.json',
          endpoints: {}
        },
        rate_limits: {
          requests_per_hour: 1000,
          burst_limit: 50
        }
      },
      propublica: {
        authentication: {
          type: 'api_key',
          credentials: {
            api_key: ''
          },
          headers: {
            'X-API-Key': '{api_key}'
          }
        },
        endpoint: {
          base_url: 'https://api.propublica.org/congress/v1',
          endpoints: {
            members: '/members/senate/CO/current.json',
            bills: '/bills/search.json'
          }
        },
        rate_limits: {
          requests_per_hour: 5000,
          burst_limit: 10
        }
      },
      congress: {
        authentication: {
          type: 'api_key',
          credentials: {
            api_key: ''
          },
          headers: {
            'X-API-Key': '{api_key}'
          }
        },
        endpoint: {
          base_url: 'https://api.congress.gov/v3',
          endpoints: {
            bills: '/bill',
            members: '/member'
          }
        },
        rate_limits: {
          requests_per_hour: 5000,
          burst_limit: 20
        }
      },
      opensecrets: {
        authentication: {
          type: 'api_key',
          credentials: {
            api_key: ''
          },
          headers: {}
        },
        endpoint: {
          base_url: 'https://www.opensecrets.org/api',
          endpoints: {
            legislators: '/?method=getLegislators&output=json',
            contributors: '/?method=candContrib&output=json'
          }
        },
        rate_limits: {
          requests_per_hour: 200,
          burst_limit: 5
        }
      }
    };

    return presets[apiName] || {};
  };

  const handlePresetSelect = (presetName: string) => {
    const preset = getPresetConfig(presetName);
    const updatedConfig = {
      ...config,
      ...preset,
      authentication: {
        ...config.authentication,
        ...preset.authentication
      },
      endpoint: {
        ...config.endpoint,
        ...preset.endpoint
      },
      rate_limits: {
        ...config.rate_limits,
        ...preset.rate_limits
      }
    };
    setConfig(updatedConfig);
    onChange(updatedConfig);
  };

  const updateConfig = (path: string[], value: any) => {
    const updatedConfig = { ...config };
    let current = updatedConfig;
    
    for (let i = 0; i < path.length - 1; i++) {
      if (!current[path[i]]) {
        current[path[i]] = {};
      }
      current = current[path[i]];
    }
    
    current[path[path.length - 1]] = value;
    setConfig(updatedConfig);
    onChange(updatedConfig);
  };

  const handleTestConnection = async () => {
    if (!onTest) return;
    
    setTesting(true);
    setTestResult(null);
    
    try {
      const result = await onTest(config);
      setTestResult(result);
    } catch (error: any) {
      setTestResult({
        success: false,
        error: error.message || 'Test failed'
      });
    } finally {
      setTesting(false);
    }
  };

  const toggleCredentialVisibility = (field: string) => {
    setShowCredentials(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const isApiType = dataSourceType.toLowerCase().includes('api');

  if (!isApiType) {
    return (
      <div className="p-4 bg-background-secondary rounded-lg border border-border-light">
        <p className="text-neutral-600 text-sm">
          API configuration is only available for API-type data sources.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* API Preset Selection */}
      <div>
        <label className="block text-sm font-medium text-neutral-700 mb-2">
          Government API Presets
        </label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {['colorado', 'propublica', 'congress', 'opensecrets'].map((preset) => (
            <button
              key={preset}
              type="button"
              onClick={() => handlePresetSelect(preset)}
              className="px-3 py-2 text-sm border border-border-light-secondary rounded-md hover:bg-blue-50 hover:border-blue-300 transition-colors"
              disabled={disabled}
            >
              {preset.charAt(0).toUpperCase() + preset.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Authentication Configuration */}
      <div className="border border-border-light rounded-lg p-4">
        <h3 className="text-lg font-medium text-neutral-900 mb-4">Authentication</h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Authentication Type
            </label>
            <select
              value={config.authentication.type}
              onChange={(e) => updateConfig(['authentication', 'type'], e.target.value)}
              className="input w-full"
              disabled={disabled}
            >
              <option value="none">None (Public Access)</option>
              <option value="api_key">API Key</option>
              <option value="bearer_token">Bearer Token</option>
              <option value="basic_auth">Basic Authentication</option>
              <option value="oauth">OAuth 2.0</option>
              <option value="custom">Custom Headers</option>
            </select>
          </div>

          {/* API Key */}
          {config.authentication.type === 'api_key' && (
            <>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  API Key
                </label>
                <div className="relative">
                  <input
                    type={showCredentials.api_key ? 'text' : 'password'}
                    value={config.authentication.credentials.api_key || ''}
                    onChange={(e) => updateConfig(['authentication', 'credentials', 'api_key'], e.target.value)}
                    className="input w-full pr-10"
                    placeholder="Enter your API key"
                    disabled={disabled}
                  />
                  <button
                    type="button"
                    onClick={() => toggleCredentialVisibility('api_key')}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    {showCredentials.api_key ? (
                      <EyeOff className="h-4 w-4 text-neutral-500" />
                    ) : (
                      <Eye className="h-4 w-4 text-neutral-500" />
                    )}
                  </button>
                </div>
              </div>

              {/* API Secret - optional for most Socrata APIs */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  API Secret <span className="text-neutral-500 text-xs">(optional for Socrata)</span>
                </label>
                <div className="relative">
                  <input
                    type={showCredentials.api_secret ? 'text' : 'password'}
                    value={config.authentication.credentials.api_secret || ''}
                    onChange={(e) => updateConfig(['authentication', 'credentials', 'api_secret'], e.target.value)}
                    className="input w-full pr-10"
                    placeholder="Enter API secret if your provider requires it"
                    disabled={disabled}
                  />
                  <button
                    type="button"
                    onClick={() => toggleCredentialVisibility('api_secret')}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    {showCredentials.api_secret ? (
                      <EyeOff className="h-4 w-4 text-neutral-500" />
                    ) : (
                      <Eye className="h-4 w-4 text-neutral-500" />
                    )}
                  </button>
                </div>
                <p className="text-xs text-neutral-500 mt-1">
                  Colorado.gov uses Socrata App Tokens (just the API key). The secret field is optional for most Socrata APIs.
                </p>
              </div>
            </>
          )}

          {/* Bearer Token */}
          {config.authentication.type === 'bearer_token' && (
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">
                Bearer Token
              </label>
              <div className="relative">
                <input
                  type={showCredentials.bearer_token ? 'text' : 'password'}
                  value={config.authentication.credentials.bearer_token || ''}
                  onChange={(e) => updateConfig(['authentication', 'credentials', 'bearer_token'], e.target.value)}
                  className="input w-full pr-10"
                  placeholder="Enter your bearer token"
                  disabled={disabled}
                />
                <button
                  type="button"
                  onClick={() => toggleCredentialVisibility('bearer_token')}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showCredentials.bearer_token ? (
                    <EyeOff className="h-4 w-4 text-neutral-500" />
                  ) : (
                    <Eye className="h-4 w-4 text-neutral-500" />
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Basic Auth */}
          {config.authentication.type === 'basic_auth' && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Username
                </label>
                <input
                  type="text"
                  value={config.authentication.credentials.username || ''}
                  onChange={(e) => updateConfig(['authentication', 'credentials', 'username'], e.target.value)}
                  className="input w-full"
                  placeholder="Username"
                  disabled={disabled}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showCredentials.password ? 'text' : 'password'}
                    value={config.authentication.credentials.password || ''}
                    onChange={(e) => updateConfig(['authentication', 'credentials', 'password'], e.target.value)}
                    className="input w-full pr-10"
                    placeholder="Password"
                    disabled={disabled}
                  />
                  <button
                    type="button"
                    onClick={() => toggleCredentialVisibility('password')}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    {showCredentials.password ? (
                      <EyeOff className="h-4 w-4 text-neutral-500" />
                    ) : (
                      <Eye className="h-4 w-4 text-neutral-500" />
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* OAuth */}
          {config.authentication.type === 'oauth' && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Client ID
                </label>
                <input
                  type="text"
                  value={config.authentication.credentials.oauth_client_id || ''}
                  onChange={(e) => updateConfig(['authentication', 'credentials', 'oauth_client_id'], e.target.value)}
                  className="input w-full"
                  placeholder="OAuth Client ID"
                  disabled={disabled}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">
                  Client Secret
                </label>
                <div className="relative">
                  <input
                    type={showCredentials.oauth_client_secret ? 'text' : 'password'}
                    value={config.authentication.credentials.oauth_client_secret || ''}
                    onChange={(e) => updateConfig(['authentication', 'credentials', 'oauth_client_secret'], e.target.value)}
                    className="input w-full pr-10"
                    placeholder="OAuth Client Secret"
                    disabled={disabled}
                  />
                  <button
                    type="button"
                    onClick={() => toggleCredentialVisibility('oauth_client_secret')}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    {showCredentials.oauth_client_secret ? (
                      <EyeOff className="h-4 w-4 text-neutral-500" />
                    ) : (
                      <Eye className="h-4 w-4 text-neutral-500" />
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Endpoint Configuration */}
      <div className="border border-border-light rounded-lg p-4">
        <h3 className="text-lg font-medium text-neutral-900 mb-4">API Endpoint</h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Base URL
            </label>
            <input
              type="url"
              value={config.endpoint.base_url}
              onChange={(e) => updateConfig(['endpoint', 'base_url'], e.target.value)}
              className="input w-full"
              placeholder="https://api.example.com"
              disabled={disabled}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              API Version (optional)
            </label>
            <input
              type="text"
              value={config.endpoint.version || ''}
              onChange={(e) => updateConfig(['endpoint', 'version'], e.target.value)}
              className="input w-full"
              placeholder="v1"
              disabled={disabled}
            />
          </div>
        </div>
      </div>

      {/* Rate Limits */}
      <div className="border border-border-light rounded-lg p-4">
        <h3 className="text-lg font-medium text-neutral-900 mb-4">Rate Limits</h3>
        
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Requests/Hour
            </label>
            <input
              type="number"
              value={config.rate_limits.requests_per_hour || ''}
              onChange={(e) => updateConfig(['rate_limits', 'requests_per_hour'], parseInt(e.target.value) || 0)}
              className="input w-full"
              placeholder="1000"
              disabled={disabled}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Requests/Minute
            </label>
            <input
              type="number"
              value={config.rate_limits.requests_per_minute || ''}
              onChange={(e) => updateConfig(['rate_limits', 'requests_per_minute'], parseInt(e.target.value) || 0)}
              className="input w-full"
              placeholder="60"
              disabled={disabled}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Burst Limit
            </label>
            <input
              type="number"
              value={config.rate_limits.burst_limit || ''}
              onChange={(e) => updateConfig(['rate_limits', 'burst_limit'], parseInt(e.target.value) || 0)}
              className="input w-full"
              placeholder="10"
              disabled={disabled}
            />
          </div>
        </div>
      </div>

      {/* Test Connection */}
      {onTest && (
        <div className="border border-border-light rounded-lg p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-neutral-900">Connection Test</h3>
            <Button
              type="button"
              onClick={handleTestConnection}
              disabled={testing || disabled || !config.endpoint.base_url}
              variant="primary"
              className="flex items-center gap-2"
            >
              {testing ? (
                <>
                  <Clock className="h-4 w-4 animate-spin" />
                  Testing...
                </>
              ) : (
                <>
                  <TestTube className="h-4 w-4" />
                  Test Connection
                </>
              )}
            </Button>
          </div>

          {testResult && (
            <div className={`p-3 rounded-lg border ${
              testResult.success 
                ? 'bg-green-50 border-green-200' 
                : 'bg-red-50 border-red-200'
            }`}>
              <div className="flex items-center gap-2 mb-2">
                {testResult.success ? (
                  <CheckCircle className="h-5 w-5 text-green-600" />
                ) : (
                  <XCircle className="h-5 w-5 text-red-600" />
                )}
                <span className={`font-medium ${
                  testResult.success ? 'text-green-800' : 'text-red-800'
                }`}>
                  {testResult.success ? 'Connection Successful' : 'Connection Failed'}
                </span>
              </div>
              
              {testResult.success && (
                <div className="text-sm text-green-700">
                  <p>Response time: {testResult.response_time || 0}ms</p>
                  {testResult.status && <p>Status: {testResult.status}</p>}
                </div>
              )}
              
              {testResult.error && (
                <div className="text-sm text-red-700">
                  <p>Error: {testResult.error}</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ApiConfigurationForm;